import { createSlice, PayloadAction } from "@reduxjs/toolkit";

interface OtpAuthState {
    loading: boolean;
    error: string | null;
    token: { access: string; refresh?: string } | null;
    userExists: boolean;
    otpSent: boolean;
    otpVerified: boolean;
    email: string | null;
    message: string | null;
    otpResendLoading: boolean;
    resendCooldown: number;
}

const initialState: OtpAuthState = {
    loading: false,
    error: null,
    token: null,
    userExists: false,
    otpSent: false,
    otpVerified: false,
    email: null,
    message: null,
    otpResendLoading: false,
    resendCooldown: 0,
};

const otpAuthSlice = createSlice({
    name: "otpAuth",
    initialState,
    reducers: {
        sendOtpRequest: (state, action: PayloadAction<{ email: string }>) => {
            state.loading = true;
            state.error = null;
            state.otpSent = false;
            state.email = action.payload.email;
        },
        sendOtpSuccess: (state) => {
            state.loading = false;
            state.otpSent = true;
            state.error = null;
        },
        sendOtpFailure: (state, action: PayloadAction<string>) => {
            state.loading = false;
            state.error = action.payload;
            state.otpSent = false;
        },
        verifyOtpRequest: (state, action: PayloadAction<{ email: string; otp: string }>) => {
            state.loading = true;
            state.error = null;
        },
        verifyOtpSuccess: (
            state,
            action: PayloadAction<{
                message: string;
                token: { access: string; refresh?: string };
                user_exists: boolean;
            }>,
        ) => {
            state.loading = false;
            state.token = action.payload.token;
            state.userExists = action.payload.user_exists;
            state.message = action.payload.message;
            state.otpVerified = true;
            state.error = null;
        },
        verifyOtpFailure: (state, action: PayloadAction<string>) => {
            state.loading = false;
            state.error = action.payload;
            state.otpVerified = false;
        },
        resendOtpRequest: (state, action: PayloadAction<{ email: string }>) => {
            state.otpResendLoading = true;
            state.error = null;
            state.resendCooldown = 30;
        },
        resendOtpSuccess: (state) => {
            state.otpResendLoading = false;
            state.error = null;
        },
        resendOtpFailure: (state, action: PayloadAction<string>) => {
            state.otpResendLoading = false;
            state.error = action.payload;
        },
        decrementResendCooldown: (state) => {
            if (state.resendCooldown > 0) {
                state.resendCooldown -= 1;
            }
        },
        clearOtpAuthError: (state) => {
            state.error = null;
        },
        resetOtpAuthState: () => initialState,
        // Social login actions
        googleLoginRequest: (state) => {
            state.loading = true;
            state.error = null;
        },
        appleLoginRequest: (state) => {
            state.loading = true;
            state.error = null;
        },
        socialLoginFailure: (state, action: PayloadAction<string>) => {
            state.loading = false;
            state.error = action.payload;
        },
    },
});

export const {
    sendOtpRequest,
    sendOtpSuccess,
    sendOtpFailure,
    verifyOtpRequest,
    verifyOtpSuccess,
    verifyOtpFailure,
    resendOtpRequest,
    resendOtpSuccess,
    resendOtpFailure,
    decrementResendCooldown,
    clearOtpAuthError,
    resetOtpAuthState,
    googleLoginRequest,
    appleLoginRequest,
    socialLoginFailure,
} = otpAuthSlice.actions;

export const otpAuthReducer = otpAuthSlice.reducer;
export const otpAuthActionTypes = otpAuthSlice.actions;
